import React, {useEffect} from "react";
import {
    IonButton,
    IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonCol,
    IonContent, IonGrid, IonIcon, IonInput, IonItem, IonLabel,
    IonLoading,
    IonPage, IonRow, IonSelect, IonSelectOption, useIonModal
} from "@ionic/react";
import {
    documentOutline,
    documentSharp,
    searchOutline,
    searchSharp
} from "ionicons/icons";
import './Aca.css';
import {useHistory} from "react-router";
import AcaChart, {CarrierSummaryChart} from "./aca/components/AcaChart";
import {useMutation} from "@apollo/client";
import {ACA_GET_SUMMARY_INFO} from "../schemas/aca";
import {RecentOpportunityChanges} from "./aca/types";
import {formatDatetime, formatStatus} from "../utils";
import useAuth from "../hooks/useAuth";
import {AcaHeader} from "./aca/AcaHeader";


const Aca: React.FC<any> = () => {
    const {claims } = useAuth();
    const history = useHistory();
    const DEFAULT_LOADING_MESSAGE: string = 'Please wait...';
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string>(DEFAULT_LOADING_MESSAGE);

    const [timeFrame, setTimeFrame] = React.useState<'TODAY' | 'THIS_WEEK' | 'LAST_WEEK' | 'THIS_MONTH' | 'LAST_MONTH' | 'ALL_TIME'>('ALL_TIME');
    const [tier1, setTier1] = React.useState<boolean>(false);
    const [tier2, setTier2] = React.useState<boolean>(false);
    const [manager, setManager] = React.useState<boolean>(false);

    const [chart1, setChart1] = React.useState<CarrierSummaryChart | undefined>(undefined);
    const [chart2, setChart2] = React.useState<CarrierSummaryChart | undefined>(undefined);
    const [chart3, setChart3] = React.useState<CarrierSummaryChart | undefined>(undefined);
    const [opportunityRecentChanges, setOpportunityRecentChanges] = React.useState<RecentOpportunityChanges[] | undefined>(undefined);
    const [opportunityRecentChangesByUser, setOpportunityRecentChangesByUser] = React.useState<RecentOpportunityChanges[] | undefined>(undefined);

    const [quickSearch, setQuickSearch] = React.useState<string | undefined | null>(undefined);
    const showDetails = async (id: number) => {
        history.push('/aca/view-opportunity/'+id);
    };


    const [acaGetSummaryInfoMutation] = useMutation(ACA_GET_SUMMARY_INFO);

    const performQuickSearch = async () => {
        if (!quickSearch) {
            return alert('Please enter a first name, last name, phone, or email in the quick search input');
        }
        if (quickSearch.length < 1) {
            return alert('Please enter 1 or more characters to begin searching');
        }
        return history.push('/aca/search/'+quickSearch);
    };


    const loadData = async (timeFrame: 'TODAY' | 'THIS_WEEK' | 'LAST_WEEK' | 'THIS_MONTH' | 'LAST_MONTH' | 'ALL_TIME') => {
        try {
            let lookupResult = await acaGetSummaryInfoMutation({variables: {timeFrame: timeFrame}});

            if (lookupResult.data?.acaGetSummaryInfo.contactSummary) {
                const contactSummary = lookupResult.data.acaGetSummaryInfo.contactSummary;
                const opportunityStagesSummary = lookupResult.data.acaGetSummaryInfo.opportunityStagesSummary;
                const opportunityStatusesSummary = lookupResult.data.acaGetSummaryInfo.opportunityStatusesSummary;
                setChart1({
                    title: 'Contacts',
                    subtitle: '',
                    data: {
                        labels: contactSummary.map((item: {count: number,status: string}) => item.status),
                        datasets: [
                            {
                                data: contactSummary.map((item: {count: number,status: string}) => Math.round(item.count)),
                                backgroundColor: contactSummary.map((item: {count: number,status: string}, index:number) => 'rgba(22, 143, 164, '+((index+1)*.18)+')'),
                                borderColor: contactSummary.map((item: {count: number,status: string}) => 'rgba(22, 143, 164)'),
                                borderWidth: 1,
                                dataVisibility: new Array(contactSummary.length).fill(true),
                            },
                        ]
                    },
                    options: {

                        type: 'doughnut',
                        plugins: {
                            tooltip: {
                                titleFont: {
                                    size: 14
                                },
                                bodyFont: {
                                    size: 12
                                },
                            },
                            legend: {
                                display: true,
                                responsive: true,
                                position: "bottom",
                                labels: {
                                    boxWidth: 36,
                                    padding: 5,
                                    font: {
                                        size: 12
                                    },
                                },
                                align: "center",
                            },
                        }
                    }
                });
                setChart2({
                    title: 'Opportunity Stages',
                    subtitle: '',
                    data: {
                        labels: opportunityStagesSummary.map((item: {count: number,name: string}) => item.name),
                        datasets: [
                            {
                                data: opportunityStagesSummary.map((item: {count: number,status: string}) => Math.round(item.count)),
                                backgroundColor: opportunityStagesSummary.map((item: {count: number,status: string}, index:number) => 'rgba(22, 143, 164, '+((index+1)*.18)+')'),
                                borderColor: opportunityStagesSummary.map((item: {count: number,status: string}) => 'rgba(22, 143, 164)'),
                                borderWidth: 1,
                                dataVisibility: new Array(opportunityStagesSummary.length).fill(true),
                            },
                        ]
                    },
                    options: {

                        type: 'doughnut',
                        plugins: {
                            tooltip: {
                                titleFont: {
                                    size: 14
                                },
                                bodyFont: {
                                    size: 12
                                },
                            },
                            legend: {
                                display: true,
                                responsive: true,
                                position: "bottom",
                                labels: {
                                    boxWidth: 36,
                                    padding: 5,
                                    font: {
                                        size: 12
                                    },
                                },
                                align: "center",
                            },
                        }
                    }
                });
                setChart3({
                    title: 'Opportunity Statuses',
                    subtitle: '',
                    data: {
                        labels: opportunityStatusesSummary.map((item: {count: number,status: string}) => item.status),
                        datasets: [
                            {
                                data: opportunityStatusesSummary.map((item: {count: number,status: string}) => Math.round(item.count)),
                                backgroundColor: opportunityStatusesSummary.map((item: {count: number,status: string}, index:number) => 'rgba(22, 143, 164, '+((index+1)*.18)+')'),
                                borderColor: opportunityStatusesSummary.map((item: {count: number,status: string}) => 'rgba(22, 143, 164)'),
                                borderWidth: 1,
                                dataVisibility: new Array(opportunityStatusesSummary.length).fill(true),
                            },
                        ]
                    },
                    options: {

                        type: 'doughnut',
                        plugins: {
                            tooltip: {
                                titleFont: {
                                    size: 14
                                },
                                bodyFont: {
                                    size: 12
                                },
                            },
                            legend: {
                                display: true,
                                responsive: true,
                                position: "bottom",
                                labels: {
                                    boxWidth: 36,
                                    padding: 5,
                                    font: {
                                        size: 12
                                    },
                                },
                                align: "center",
                            },
                        }
                    }
                });
                setOpportunityRecentChanges(lookupResult.data.acaGetSummaryInfo.opportunityRecentChanges);
                setOpportunityRecentChangesByUser(lookupResult.data.acaGetSummaryInfo.opportunityRecentChangesByUser);
            }

            return null;
        } catch (e) {
            console.log("error:");
            console.log(e);
            alert("Error: " + e);
            return null;
        } finally {
            setIsLoading(false);
        }
    };


    useEffect(() => {
        // check for roles
        if (!claims.rules.includes('AcaView')) {
            history.push('/');
            return;
        }
        if (claims.rules.includes('AcaEditTier2')) {
            setTier2(true);
        }
        if (claims.rules.includes('AcaEditTier1')) {
            setTier1(true);
        }
        if (claims.rules.includes('AcaManager')) {
            setManager(true);
        }

        setIsLoading(true);
        loadData('ALL_TIME');
    }, []);
    useEffect( () => {
        setIsLoading(true);
        loadData(timeFrame);
    }, [timeFrame]);

    return (
        <IonPage>

            <AcaHeader pageTitle={"ACA Healthcare Enrollments"}></AcaHeader>
            <IonLoading isOpen={isLoading} message={loadingMessage}/>
            <IonContent fullscreen className="aca-healthcare">
                <p style={{paddingLeft: '10px'}}><b>Your User Role:</b> {
                    manager && <>HC Manager</>
                }{
                    !manager && tier2 && <>HC Tier 2</>
                }{
                    !manager && !tier2 && tier1 && <>HC Tier 1</>
                }{
                    !manager && !tier2 && !tier1 && <>Viewing User</>
                }</p>
                <p style={{paddingLeft: '10px'}}></p>
                <div style={{paddingLeft: '10px'}}><b>Search Contacts:</b>
                    <div style={{
                        marginLeft: '5px',
                        marginRight: '5px',
                        display: "inline-block",
                        width: '200px',
                        border: '1px solid #cccc',
                        borderRadius: '5px'
                    }}><IonInput type="text" onIonChange={(e) => setQuickSearch(e.detail.value)}></IonInput></div>
                    <IonButton fill="outline" style={{
                        verticalAlign: 'top',
                        display: 'inline-block'
                    }} onClick={() => performQuickSearch()}><IonIcon md={searchOutline} ios={searchSharp}></IonIcon></IonButton>
                    <a href="/aca/advanced-search" style={{marginLeft: '5px'}}>Advanced Search</a>
                </div>

                <div style={{display: 'inline-block', width: '400px'}}>
                    <IonItem>
                        <IonLabel>Filter Data</IonLabel>
                        <IonSelect value={timeFrame} onIonChange={(e) => setTimeFrame(e.detail.value)}>
                            <IonSelectOption value="TODAY">Today</IonSelectOption>
                            <IonSelectOption value="THIS_WEEK">This Week</IonSelectOption>
                            <IonSelectOption value="LAST_WEEK">Last Week</IonSelectOption>
                            <IonSelectOption value="THIS_MONTH">This Month</IonSelectOption>
                            <IonSelectOption value="LAST_MONTH">Last Month</IonSelectOption>
                            <IonSelectOption value="ALL_TIME">All Time</IonSelectOption>
                        </IonSelect>
                    </IonItem>
                </div>
                <div>

                    {chart1 && chart1.data.datasets.length > 0 &&

                        <AcaChart type="doughnut" key="chart1" chart={chart1} style={{
                            width: '30%',
                            float: 'left'
                        }}></AcaChart>
                    }

                    {chart3 && chart3.data.datasets.length > 0 &&
                        <AcaChart type="doughnut" key="chart3" chart={chart3} style={{
                            width: '30%',
                            float: 'left'
                        }}></AcaChart>
                    }
                    {chart2 && chart2.data.datasets.length > 0 &&
                        <AcaChart type="doughnut" key="chart2" chart={chart2} style={{
                            width: '30%',
                            float: 'left'
                        }}></AcaChart>
                    }
                    <div style={{clear: 'both'}}></div>
                </div>


                {opportunityRecentChanges &&
                    <IonCard style={{}}>
                        <IonCardHeader>
                            <IonGrid style={{width:'100%'}}>
                                <IonRow>
                                    <IonCol>
                                        <IonCardTitle style={{float: 'left', margin: '0px'}}>Opportunity Pipeline</IonCardTitle>
                                    </IonCol>
                                    <IonCol style={{textAlign: 'right'}}>
                                        <a href="/aca/pipeline/1" style={{}}>View Pipeline</a>
                                    </IonCol>
                                </IonRow>
                            </IonGrid>
                        </IonCardHeader>
                        <IonCardContent>
                            <p>Recent Opportunity Activity:</p>
                            <div style={{overflow: 'auto', maxHeight: '400px', display: 'inline-block', width: '100%'}}>
                                <table style={{width: '100%'}} className="aca-healthcare">
                                    <thead>
                                    <tr>
                                        <th>Opp ID</th>
                                        <th>Contact Name</th>
                                        <th>Last Updated</th>
                                        <th>Created Datetime</th>
                                        <th>Assigned</th>
                                        <th>State</th>
                                        <th>Stage</th>
                                        <th>Status</th>
                                        <th>Action</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {opportunityRecentChanges.map((change, index) => {
                                        return (
                                            <tr key={'opp1_' + change.opportunityId}>
                                                <td style={{textAlign: 'center'}}>{change.opportunityId}</td>
                                                <td>
                                                    <a href={'/aca/view-opportunity/' + change.opportunityId}>{change.firstName} {change.lastName}</a>
                                                </td>
                                                <td style={{textAlign: 'center'}}>{formatDatetime(change.lastUpdatedDatetime)}</td>
                                                <td style={{textAlign: 'center'}}>{formatDatetime(change.createdDatetime)}</td>
                                                <td>{change.assignedUserName}</td>
                                                <td style={{textAlign: 'center'}}>{change.addressState}</td>
                                                <td style={{textAlign: 'center'}}>{change.stage}</td>
                                                <td style={{textAlign: 'center'}}>{formatStatus(change.opportunityStatus)}</td>
                                                <td style={{textAlign: 'center'}}>
                                                    <IonButton fill="clear" className="xs-button" routerDirection="none" routerLink={'/aca/view-opportunity/' + change.opportunityId} title="Details"><IonIcon md={documentOutline} ios={documentSharp}></IonIcon></IonButton>&nbsp;

                                                </td>
                                            </tr>
                                        )
                                    })}

                                    </tbody>
                                </table>
                            </div>
                        </IonCardContent>
                    </IonCard>
                }

                {tier2 && opportunityRecentChangesByUser && !manager &&
                    <IonCard style={{}}>
                        <IonCardHeader>
                            <IonCardTitle style={{float: 'left', margin: '0px'}}>My Recent Opportunity
                                Activity </IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                            <table style={{width: '100%', marginTop: '20px'}} className="aca-healthcare">
                                <thead>
                                <tr>
                                    <th>Opp ID</th>
                                    <th>Contact Name</th>
                                    <th>Last Updated</th>
                                    <th>Created Datetime</th>
                                    <th>Assigned</th>
                                    <th>State</th>
                                    <th>Stage</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                                </thead>
                                <tbody>
                                {opportunityRecentChangesByUser.map((change, index) => {
                                    return (
                                        <tr key={'opp2_'+change.opportunityId}>
                                            <td style={{textAlign: 'center'}}>{change.opportunityId}</td>
                                            <td><a href={'/aca/view-opportunity/' + change.opportunityId}>{change.firstName} {change.lastName}</a></td>
                                            <td style={{textAlign: 'center'}}>{formatDatetime(change.lastUpdatedDatetime)}</td>
                                            <td style={{textAlign: 'center'}}>{formatDatetime(change.createdDatetime)}</td>
                                            <td>{change.assignedUserName}</td>
                                            <td style={{textAlign: 'center'}}>{change.addressState}</td>
                                            <td style={{textAlign: 'center'}}>{change.stage}</td>
                                            <td style={{textAlign: 'center'}}>{formatStatus(change.opportunityStatus)}</td>
                                            <td style={{textAlign: 'center'}}>
                                                <IonButton fill="clear" className="xs-button" routerDirection="none" routerLink={'/aca/view-opportunity/' + change.opportunityId} title="Opportunity Details"><IonIcon md={documentOutline} ios={documentSharp}></IonIcon></IonButton>&nbsp;

                                             </td>
                                        </tr>
                                    )
                                })}
                                </tbody>
                            </table>
                        </IonCardContent>
                    </IonCard>
                }

                <p>&nbsp;</p><p>&nbsp;</p><p>&nbsp;</p><p>&nbsp;</p>

            </IonContent>
        </IonPage>
    );
};

export default Aca;